import { useQuery } from '@apollo/client';
import type { Pills } from '@seek/chalice-types';
import { useHubble } from '@seek/hubble';
import type React from 'react';
import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
} from 'react';

import {
  GetJobSearchAssistDocument,
  type JobSearchAssistPills,
} from 'src/graphql/graphql';
import { logger } from 'src/modules/logger';
import { useFeatureFlagRefineBarV2Experiment } from 'src/store/featureFlags/hooks';
import { useDispatch, useSelector } from 'src/store/react';
import {
  clearStateToken,
  updateBaseKeywordParam,
  updateKeywordsField,
  updateStateToken,
} from 'src/store/search';
import { selectBaseKeywords } from 'src/store/search/selectors';
import { useRunSearch } from 'src/store/search/useRunSearch';
import {
  selectFeatureFlag,
  selectPills,
  selectIsResultsLoading,
  selectAsyncPillsToken,
  selectSolId,
} from 'src/store/selectors';

export type JobSearchAssist = {
  isJobSearchAssistEnabled: boolean;
  isLoading: boolean;
  pills: Pills | JobSearchAssistPills['data'];
  resetState: ({ keywords }: { keywords?: string }) => void;
  onPillClick: ({
    keywords,
    stateToken,
  }: {
    keywords: string;
    stateToken?: string;
  }) => void;
};

// Create context with a default value
const JobSearchAssistContext = createContext<JobSearchAssist>({
  isJobSearchAssistEnabled: false,
  isLoading: false,
  pills: [],
  resetState: () => {},
  onPillClick: () => {},
});

// Provider component that wraps parts of your app that need JobSearchAssist
export const JobSearchAssistProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();

  const _isJobSearchAssistEnabled = useSelector(
    selectFeatureFlag('isJobSearchAssistEnabled'),
  );
  const runSearch = useRunSearch();

  /**
   * Visitor ID and Token Management Strategy:
   *
   * - serverVisitorId: Persisted from server-side rendering (SSR)
   * - clientVisitorId: Generated dynamically on the client-side
   * - asyncPillsToken: Associated with serverVisitorId during SSR
   *
   * We must use serverVisitorId for the initial request to maintain token validity,
   * then transition to clientVisitorId for all subsequent requests on the client-side to ensure
   * proper job assist functionality.
   */

  const serverVisitorId = useSelector(selectSolId);
  const clientVisitorId = useHubble().visitorId();

  // Memoize the tokens array to prevent unnecessary re-renders
  const asyncPillsToken = useSelector(selectAsyncPillsToken);
  const tokens = useMemo(() => [asyncPillsToken], [asyncPillsToken]);

  const currentVisitorId = useRef(serverVisitorId);

  const baseKeywords = useSelector(selectBaseKeywords);
  const pills = useSelector(selectPills);
  const isLoading = useSelector(selectIsResultsLoading);

  const { refineBarV2, dynamicPillsV2 } = useFeatureFlagRefineBarV2Experiment();
  const isJobSearchAssistEnabled =
    _isJobSearchAssistEnabled &&
    asyncPillsToken &&
    (refineBarV2 || dynamicPillsV2);

  const queryOptions = useMemo(
    () => ({
      variables: {
        tokens,
        visitorId: currentVisitorId.current,
      },
      context: {
        headers: {
          'x-custom-features': 'application/features.seek.all+json',
        },
      },
      fetchPolicy: 'cache-and-network' as const,
      skip: !isJobSearchAssistEnabled,
      ssr: false,
      onCompleted: () => {
        currentVisitorId.current = clientVisitorId;
      },
      onError: (err: Error) => {
        logger.error(
          { err, input: { tokens } },
          'JobSearchAssist GraphQL Failure',
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokens, isJobSearchAssistEnabled],
  );

  // Execute the query with the memoized options
  const { loading: isJobSearchAssistLoading, data } = useQuery(
    GetJobSearchAssistDocument,
    queryOptions,
  );

  // Memoize callbacks to maintain stable references
  const resetStateToken = useRef(() => {
    dispatch(clearStateToken());
  });

  const onJobSearchAssistPillClick = useRef(
    ({ keywords, stateToken }: { keywords: string; stateToken?: string }) => {
      if (stateToken) {
        dispatch(updateStateToken(stateToken));
      }
      dispatch(updateKeywordsField(keywords));
      dispatch(runSearch({ pathname: '/jobs' }));
    },
  );

  const resetBaseKeywordParam = useRef(
    ({ keywords = '' }: { keywords?: string } = {}) => {
      dispatch(updateBaseKeywordParam(keywords));
    },
  );

  const onPillClick = useMemo(
    () =>
      ({ keywords }: { keywords: string }) => {
        if (!baseKeywords) {
          dispatch(updateBaseKeywordParam(keywords));
        }
        dispatch(updateKeywordsField(keywords));
        dispatch(runSearch({ pathname: '/jobs' }));
      },
    [baseKeywords, dispatch, runSearch],
  );

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () =>
      isJobSearchAssistEnabled
        ? {
            isJobSearchAssistEnabled: true,
            isLoading: isJobSearchAssistLoading || isLoading,
            pills: data?.jobSearchAssist?.pills?.data || [],
            resetState: resetStateToken.current,
            onPillClick: onJobSearchAssistPillClick.current,
          }
        : {
            isJobSearchAssistEnabled: false,
            isLoading,
            pills: pills || [],
            resetState: resetBaseKeywordParam.current,
            onPillClick,
          },
    [
      isJobSearchAssistEnabled,
      isJobSearchAssistLoading,
      isLoading,
      data?.jobSearchAssist?.pills?.data,
      pills,
      onPillClick,
    ],
  );

  return (
    <JobSearchAssistContext.Provider value={contextValue}>
      {children}
    </JobSearchAssistContext.Provider>
  );
};

export const useJobSearchAssist = (): JobSearchAssist => {
  const context = useContext(JobSearchAssistContext);

  return context;
};

export default useJobSearchAssist;
